/* eslint-disable @typescript-eslint/member-ordering */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EffectRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatRipple } from '@angular/material/core';
import { IconComponent } from '@libs/components/icons';
import { CapacitorService } from '@libs/shared/common';
import { IconName } from '@libs/shared/models';
import { debounceTime, Subscription, switchMap } from 'rxjs';

export const DEFAULT_DEBOUNCE_TIME: number = 300;

const DEFAULT_SEARCH_ICON: IconName = 'magnifying-glass';

@Component({
  selector: 'ui-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IconComponent,
    MatRipple,
  ],
})
export class SearchInputComponent {
  private readonly searchInputElement: Signal<ElementRef<HTMLInputElement>> = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly searchFieldControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  protected readonly isCapacitorApp: Signal<boolean> = signal<boolean>(inject(CapacitorService).isCapacitorApp());
  protected readonly icon: Signal<IconName> = computed(() => this.customIcon() ?? DEFAULT_SEARCH_ICON);

  public readonly searchText: InputSignal<string> = input<string>('');
  public readonly customIcon: InputSignal<IconName | null> = input<IconName | null>(null);
  public readonly placeholder: InputSignal<string> = input<string>('');
  public readonly readOnly: InputSignal<boolean> = input<boolean>(false);
  public readonly validators: InputSignal<ValidatorFn | ValidatorFn[] | null> = input<ValidatorFn | ValidatorFn[] | null>(null);
  public readonly debounceTime: InputSignal<number> = input<number>(DEFAULT_DEBOUNCE_TIME);
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);
  public readonly barcodeClicked: OutputEmitterRef<void> = output<void>();
  public readonly searchSubmit: OutputEmitterRef<string> = output<string>();
  public readonly searchChanged: OutputEmitterRef<string> = output<string>();
  public readonly customIconClicked: OutputEmitterRef<void> = output<void>();
  public readonly validationErrorsChanged: OutputEmitterRef<ValidationErrors | null> = output<ValidationErrors | null>();
  public readonly focused: OutputEmitterRef<void> = output();

  private readonly syncTextWithInput: EffectRef = effect(() => {
    this.searchFieldControl.setValue(this.searchText(), { emitEvent: false });
  });

  private readonly syncValidatorsWithInput: EffectRef = effect(() => {
    this.searchFieldControl.setValidators(this.validators());
    this.searchFieldControl.updateValueAndValidity();
  });

  private readonly validationErrorsSubscription: Subscription = this.searchFieldControl.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => {
    this.validationErrorsChanged.emit(this.searchFieldControl.errors);
  });

  private readonly debouncedSearchChangedSubscription: Subscription = toObservable(this.debounceTime).pipe(
    switchMap(ms => this.searchFieldControl.valueChanges.pipe(debounceTime(ms))),
    takeUntilDestroyed(),
  ).subscribe((value) => {
    this.searchChanged.emit(value);
  });

  protected handleKeyDownEnter(): void {
    this.searchSubmit.emit(this.searchFieldControl.value);
  }

  protected handleClearInputClicked(): void {
    this.searchFieldControl.setValue('');

    setTimeout(() => {
      this.focusInput();
    });
  }

  protected handleLeadingIconClicked(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.customIcon() !== null) {
      this.customIconClicked.emit();

      return;
    }

    this.searchSubmit.emit(this.searchFieldControl.value);
  }

  protected handleBarcodeClicked(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.barcodeClicked.emit();
  }

  public blurInput(): void {
    this.searchInputElement().nativeElement.blur();
  }

  private focusInput(): void {
    this.searchInputElement().nativeElement.focus();
  }
}
